(function () {
  const EASE = 'cubic-bezier(0.42, 0, 0.58, 1)';
  const DURATION = 300;
  const SCROLL_GUARD = 700;
  const DESKTOP_BP = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-lg'), 10
  ) || 1024;

  class TabbedContent {
    constructor(root) {
      this.root     = root;
      this.triggers = [...root.querySelectorAll('[data-tab-trigger]')];
      this.panels   = [...root.querySelectorAll('[data-tab-panel]')];
      this.cards    = [...root.querySelectorAll('[data-tab-card]')];
      this.track    = root.querySelector('[data-tab-carousel-track]');
      this.list     = root.querySelector('[data-tab-list]');
      this.prevBtns = [...root.querySelectorAll('[data-tab-prev]')];
      this.nextBtns = [...root.querySelectorAll('[data-tab-next]')];
      this.active = 0;
      this.busy = false;
      this.scrollTimer = null;

      if (!this.triggers.length) return;
      this.bindEvents();
      this.observeCarousel();
      this.updatePrevNext();
    }

    bindEvents() {
      this.triggers.forEach((t) => {
        t.addEventListener('click', () => {
          const idx = parseInt(t.dataset.tabIndex, 10);
          if (idx !== this.active) this.activate(idx);
        });
      });

      const list = this.root.querySelector('[data-tab-list]');
      if (list) {
        list.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); this.focusTab(this.active + 1); }
          if (e.key === 'ArrowLeft')  { e.preventDefault(); this.focusTab(this.active - 1); }
          if (e.key === 'Home')       { e.preventDefault(); this.focusTab(0); }
          if (e.key === 'End')        { e.preventDefault(); this.focusTab(this.triggers.length - 1); }
        });
      }

      this.prevBtns.forEach((btn) => btn.addEventListener('click', () => {
        if (this.active > 0) this.activate(this.active - 1);
      }));
      this.nextBtns.forEach((btn) => btn.addEventListener('click', () => {
        if (this.active < this.triggers.length - 1) this.activate(this.active + 1);
      }));
    }

    observeCarousel() {
      if (!this.track || !this.cards.length || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        if (this.busy) return;
        const top = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!top) return;
        const idx = parseInt(top.target.dataset.tabIndex, 10);
        if (Number.isFinite(idx) && idx !== this.active) this.setTabActive(idx);
      }, { root: this.track, threshold: 0.6 });
      this.cards.forEach((card) => io.observe(card));
    }

    focusTab(idx) {
      const clamped = Math.max(0, Math.min(this.triggers.length - 1, idx));
      this.triggers[clamped].focus();
      this.activate(clamped);
    }

    setTabActive(incomingIdx) {
      this.triggers.forEach((t, i) => {
        const isActive = i === incomingIdx;
        t.setAttribute('aria-selected', String(isActive));
        t.setAttribute('data-state', isActive ? 'active' : 'inactive');
      });
      this.active = incomingIdx;
      this.updatePrevNext();
      this.revealTab(incomingIdx);
    }

    // The tab list and the card track scroll independently, so a swipe through the cards leaves the
    // tab it selects outside the list's viewport.
    revealTab(idx) {
      const tab = this.triggers[idx];
      if (!tab) return;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      // At either end, scroll the list to its limit: aligning the edge tab stops at the content box
      // and strands the list's own padding, leaving the tab flush against the edge.
      if (this.list && (idx === 0 || idx === this.triggers.length - 1)) {
        this.list.scrollTo({ left: idx === 0 ? 0 : this.list.scrollWidth, behavior });
        return;
      }
      // block: 'nearest' keeps the page itself still.
      tab.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior });
    }

    updatePrevNext() {
      const atStart = this.active === 0;
      const atEnd   = this.active === this.triggers.length - 1;
      this.prevBtns.forEach((btn) => btn.setAttribute('aria-disabled', String(atStart)));
      this.nextBtns.forEach((btn) => btn.setAttribute('aria-disabled', String(atEnd)));
    }

    activate(idx) {
      if (this.busy || idx === this.active) return;
      if (window.innerWidth >= DESKTOP_BP) this.activateDesktop(idx);
      else this.activateMobile(idx);
    }

    activateDesktop(idx) {
      this.busy = true;
      const currentPanel  = this.panels[this.active];
      const incomingPanel = this.panels[idx];
      if (!currentPanel || !incomingPanel) { this.busy = false; return; }

      const currentContent  = currentPanel.querySelector('[data-tab-content]');
      const incomingContent = incomingPanel.querySelector('[data-tab-content]');

      this.setTabActive(idx);

      currentContent.animate(
        [{ opacity: 1 }, { opacity: 0 }],
        { duration: DURATION, easing: EASE, fill: 'forwards' }
      ).onfinish = () => {
        currentPanel.classList.add('hidden');
        incomingPanel.classList.remove('hidden');
        currentContent.style.opacity = '1';
        incomingContent.style.opacity = '0';
        incomingContent.animate(
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: DURATION, easing: EASE, fill: 'forwards' }
        ).onfinish = () => {
          incomingContent.style.opacity = '';
          this.busy = false;
        };
      };
    }

    activateMobile(idx) {
      this.setTabActive(idx);
      const card = this.cards[idx];
      if (!card || !this.track) return;
      this.busy = true;
      this.track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => { this.busy = false; }, SCROLL_GUARD);
    }
  }

  function init(root) {
    root.querySelectorAll('[data-tabbed-content]').forEach((el) => new TabbedContent(el));
  }
  init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
